import React from "react";
import { ArrowUpRight, Users, Gift, Presentation } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

const communityActions = [
  {
    number: "01",
    icon: Users,
    title: "Rejoindre la communauté",
    description:
      "Ateliers citoyens, rencontres et échanges. Une fabrique collective pour celles et ceux qui font bouger leur ville.",
    action: "join",
    cta: "Rejoindre",
  },
  {
    number: "02",
    icon: Gift,
    title: "Programme de parrainage",
    description:
      "Parrainez de nouveaux utilisateurs et faites grandir le mouvement citoyen dans votre entourage.",
    action: "referral",
    cta: "Parrainer",
  },
  {
    number: "03",
    icon: Presentation,
    title: "Pitch Deck Concours 2026",
    description:
      "Participez au concours d'idées et présentez vos projets pour améliorer la vie urbaine.",
    action: "pitch",
    cta: "Participer",
  },
];

const CommunitySection = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleAction = (action: string) => {
    if (action === "join") {
      toast({
        title: "Bienvenue dans la communauté !",
        description: "Vous allez recevoir un email avec les prochaines étapes.",
      });
    } else if (action === "referral") {
      navigate("/referral-dashboard");
    } else if (action === "pitch") {
      navigate("/pitch-contest");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-2xl mb-16 md:mb-24">
        <span className="ed-eyebrow mb-5 block">06 — Communauté</span>
        <h2 className="ed-heading text-3xl md:text-5xl mb-5">
          Rejoignez la <span className="accent">communauté</span>
        </h2>
        <p className="ed-subtle text-base md:text-lg leading-relaxed max-w-lg">
          Ateliers citoyens, rencontres, parrainages et concours. Une fabrique
          collective pour celles et ceux qui font bouger leur ville.
        </p>
      </div>

      {/* Timeline — editorial single column */}
      <div className="relative">
        {/* Central spine */}
        <div className="absolute left-[27px] md:left-[31px] top-2 bottom-2 w-px bg-gradient-to-b from-[var(--ed-navy-500)] via-[var(--ed-rule)] to-transparent opacity-40" />

        <div className="space-y-16 md:space-y-20">
          {communityActions.map((item, idx) => (
            <div
              key={item.number}
              className="relative flex gap-6 md:gap-10 group ed-fade-up"
              style={{ animationDelay: `${idx * 120}ms` }}
            >
              {/* Step marker */}
              <div className="relative z-10 shrink-0">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[var(--ed-paper)] border border-[var(--ed-rule-strong)] flex items-center justify-center shadow-sm group-hover:border-[var(--ed-navy-500)] group-hover:shadow-md transition-all duration-500">
                  <span className="ed-numeric text-xl md:text-2xl text-[var(--ed-navy-800)]">
                    {item.number}
                  </span>
                </div>
              </div>

              {/* Content card */}
              <div className="pt-1 md:pt-2 flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--ed-navy-800)]/5 flex items-center justify-center">
                    <item.icon
                      size={16}
                      strokeWidth={1.5}
                      className="text-[var(--ed-navy-500)]"
                    />
                  </div>
                  <span className="font-display text-[10px] uppercase tracking-[0.28em] text-[var(--ed-navy-500)]">
                    Action
                  </span>
                </div>

                <h3 className="font-display font-semibold text-lg md:text-xl text-[var(--ed-ink)] tracking-tight mb-3">
                  {item.title}
                </h3>

                <p className="text-[rgba(10,15,31,0.60)] text-sm md:text-base leading-[1.7] font-light max-w-md mb-5">
                  {item.description}
                </p>

                <div className="ed-rule mb-5" />

                <button
                  onClick={() => handleAction(item.action)}
                  className="inline-flex items-center gap-2 font-display font-semibold text-sm text-[var(--ed-navy-800)] group-hover:gap-3 transition-all"
                >
                  {item.cta} <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editorial CTA */}
      <div className="mt-16 md:mt-24 flex items-center gap-6">
        <div className="h-px w-16 bg-[var(--ed-navy-500)]" />
        <span className="text-xs text-[rgba(10,15,31,0.40)] font-body tracking-wide">
          Gratuit · Ouvert à tous · Ensemble on va plus loin
        </span>
      </div>
    </div>
  );
};

export default CommunitySection;
